import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/api";
import { LogoWordmark } from "@/components/Logo";
import { Loader2, ArrowRight, Mail, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* ============ LEFT BRAND PANEL ============ */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden brand-gradient p-12 lg:flex xl:p-16">
        {/* depth: glow orbs */}
        <div className="pointer-events-none absolute -right-24 -top-28 h-[26rem] w-[26rem] rounded-full bg-loop/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-24 h-[30rem] w-[30rem] rounded-full bg-loop/10 blur-3xl" />
        {/* depth: top glassy highlight */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />

        {/* subtle loop / routing motif */}
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 600 800" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <g stroke="#2C78FF" strokeWidth="1.2" opacity="0.18">
            <path d="M-40 200 C 160 120, 220 360, 420 280 S 720 360, 760 220" />
            <path d="M-40 360 C 180 280, 240 520, 440 440 S 720 520, 780 380" />
            <path d="M-40 520 C 200 440, 260 680, 460 600 S 740 680, 800 540" />
          </g>
          <g fill="#FFFFFF" opacity="0.5">
            <circle cx="420" cy="280" r="3" />
            <circle cx="440" cy="440" r="3" />
            <circle cx="460" cy="600" r="3" />
          </g>
          <g stroke="#9DC2FF" strokeWidth="1" opacity="0.12">
            <path d="M520 60 C 560 120, 480 160, 520 220 C 560 280, 480 320, 520 380" />
          </g>
        </svg>

        {/* logo lockup in clean frosted chip */}
        <div className="relative z-10">
          <div className="inline-flex items-center rounded-2xl bg-white px-5 py-3 shadow-xl shadow-navy/30 ring-1 ring-white/50">
            <LogoWordmark className="h-7" />
          </div>
        </div>

        {/* headline + copy */}
        <div className="relative z-10">
          <h1 className="font-sora text-5xl font-extrabold leading-[1.04] tracking-tight text-white xl:text-6xl">
            Own every lead.<br />
            <span className="bg-gradient-to-r from-white via-white to-[#9DC2FF] bg-clip-text text-transparent">Close every loop.</span>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-sky/85">
            Your daily workspace to manage assigned leads, track next actions, and keep every opportunity moving.
          </p>
          <div className="mt-9 flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-sky/70">
            <span className="h-1.5 w-1.5 rounded-full bg-loop shadow-[0_0_10px_2px_rgba(44,120,255,0.6)]" />
            Lead ownership · Next actions · Pipeline clarity
          </div>
        </div>

        <p className="relative z-10 text-xs text-sky/55">AcquireLoop by Proffus · v1.6</p>
      </div>

      {/* ============ RIGHT FORM ============ */}
      <div className="flex w-full flex-col items-center justify-center px-6 py-10 sm:px-10 lg:w-1/2">
        <div className="w-full max-w-sm">
          {/* mobile brand header */}
          <div className="mb-9 lg:hidden">
            <LogoWordmark className="h-8" />
            <h1 className="mt-5 font-sora text-3xl font-extrabold leading-tight tracking-tight text-navy">
              Own every lead.<br /><span className="text-loop">Close every loop.</span>
            </h1>
          </div>

          <h2 className="font-sora text-2xl font-bold tracking-tight text-navy sm:text-[1.7rem]">Welcome to AcquireLoop</h2>
          <p className="mt-1.5 text-sm text-ink/60">Sign in to your AcquireLoop workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink/80">Email</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-ink/35" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@acquireloop.com"
                  className="h-12 w-full rounded-xl border border-sky bg-white pl-11 pr-3.5 text-sm text-ink shadow-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ink/35 focus:border-loop focus:ring-4 focus:ring-loop/15"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink/80">Password</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-ink/35" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 w-full rounded-xl border border-sky bg-white pl-11 pr-3.5 text-sm text-ink shadow-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ink/35 focus:border-loop focus:ring-4 focus:ring-loop/15"
                />
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-loop font-sora text-sm font-semibold text-white shadow-lg shadow-loop/30 outline-none transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-loophover hover:shadow-xl hover:shadow-loop/40 focus:ring-4 focus:ring-loop/25 active:translate-y-0 disabled:translate-y-0 disabled:opacity-60 disabled:shadow-none"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
